import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { Check, Circle, Megaphone, Pause, Play, Send, Settings2, Trash2 } from 'lucide-react';
import EmptyState from '../../components/ui/EmptyState';
import { outreachApi } from '../api/outreachApi';
import CampaignReport from '../components/CampaignReport';
import CampaignSettings from '../components/CampaignSettings';
import CampaignWizard from '../components/CampaignWizard';
import InfoHint from '../components/InfoHint';
import '../outreach.css';

// The first screen a new account lands on is empty on three pages out of
// four, so the empty one says what to do next instead of just being empty.
function FirstRunChecklist({ hasMailbox, hasContacts, onCreate, t }) {
  const steps = [
    { done: hasMailbox, label: t('outreach.firstRun.mailbox'), to: '/outreach/mailboxes' },
    { done: hasContacts, label: t('outreach.firstRun.contacts'), to: '/outreach/contacts' },
    { done: false, label: t('outreach.firstRun.campaign'), onClick: onCreate },
  ];
  return (
    <ul className="checklist">
      {steps.map((step) => {
        const content = (
          <>
            {step.done ? <Check size={16} /> : <Circle size={16} />}
            <span className="checklist-item-label">{step.label}</span>
          </>
        );
        return (
          <li key={step.label}>
            {step.to ? (
              <Link className={`checklist-item${step.done ? ' is-done' : ''}`} to={step.to}>
                {content}
              </Link>
            ) : (
              <button
                type="button"
                className="checklist-item"
                onClick={step.onClick}
                disabled={!hasMailbox}
              >
                {content}
              </button>
            )}
          </li>
        );
      })}
    </ul>
  );
}

export default function CampaignsPage() {
  const { t } = useTranslation('common');

  const [campaigns, setCampaigns] = useState([]);
  const [stats, setStats] = useState({});
  const [accounts, setAccounts] = useState([]);
  const [lists, setLists] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showWizard, setShowWizard] = useState(false);
  const [editing, setEditing] = useState(null);
  const [reports, setReports] = useState([]);


  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [items, accountList, contactLists] = await Promise.all([
        outreachApi.listCampaigns(),
        outreachApi.listAccounts(),
        outreachApi.listContactLists(),
      ]);
      setCampaigns(items);
      setAccounts(accountList);
      setLists(contactLists);
      const entries = await Promise.all(
        items.map(async (c) => [c.id, await outreachApi.campaignStats(c.id)])
      );
      setStats(Object.fromEntries(entries));
      setReports((await outreachApi.workspaceAnalytics()).campaigns);
    } catch (e) {
      setError(e?.response?.data?.detail || e.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const changeStatus = async (campaign, status) => {
    await outreachApi.setCampaignStatus(campaign.id, status);
    await load();
  };

  const remove = async (campaign) => {
    if (!window.confirm(t('outreach.campaign.deleteConfirm', { name: campaign.name }))) return;
    await outreachApi.deleteCampaign(campaign.id);
    await load();
  };

  const sendNow = async () => {
    const result = await outreachApi.dispatchNow();
    window.alert(t('outreach.campaign.dispatched', { count: result.sent }));
    await load();
  };

  return (
    <div className="unibox">
      <div className="toolbar">
        <h1 className="toolbar-title">{t('outreach.campaign.title')}</h1>
        <div className="toolbar-filters" />
        <div className="toolbar-actions">
          <Link className="unibox-refresh" to="/outreach/contacts">
            {t('outreach.contacts')}
          </Link>
          <button className="unibox-refresh" onClick={sendNow}>
            <Send size={14} /> {t('outreach.campaign.sendNow')}
          </button>
          <button className="unibox-button" onClick={() => setShowWizard(true)} disabled={accounts.length === 0}>
            {t('outreach.campaign.create')}
          </button>
        </div>
      </div>

      {accounts.length === 0 && (
        <div className="unibox-banner">
          {t('outreach.campaign.needMailbox')} <Link to="/outreach/mailboxes">{t('outreach.mailboxes')}</Link>
        </div>
      )}

      {showWizard && (
        <CampaignWizard
          accounts={accounts}
          lists={lists}
          onDone={async () => {
            setShowWizard(false);
            await load();
          }}
          onCancel={() => setShowWizard(false)}
        />
      )}

      {editing && (
        <CampaignSettings
          campaign={editing}
          accounts={accounts}
          onSaved={async () => {
            await load();
          }}
          onClose={() => setEditing(null)}
        />
      )}

      {error && <div className="unibox-banner">{String(error)}</div>}

      <div className="unibox-detail">
        {loading && <div className="unibox-empty">{t('outreach.loading')}</div>}

        {!loading && campaigns.length === 0 && (
          <EmptyState
            icon={<Megaphone size={28} />}
            title={t('outreach.firstRun.title')}
            text={t('outreach.campaign.emptyHint')}
          >
            <FirstRunChecklist
              hasMailbox={accounts.length > 0}
              hasContacts={lists.some((list) => list.contact_count > 0)}
              onCreate={() => setShowWizard(true)}
              t={t}
            />
          </EmptyState>
        )}

        {!loading && campaigns.length > 0 && (
          <table className="admin-table" style={{ width: '100%' }}>
            <thead>
              <tr>
                <th>{t('outreach.campaign.name')}</th>
                <th>{t('outreach.status')}</th>
                <th>{t('outreach.campaign.steps')}</th>
                <th>{t('outreach.campaign.leads')}</th>
                <th>{t('outreach.campaign.sent')}</th>
                <th>{t('outreach.campaign.today')}</th>
                <th>{t('outreach.campaign.replied')}</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {campaigns.map((campaign) => {
                const stat = stats[campaign.id] || { enrollments: {}, sends: {}, sent_today: 0 };
                const atCap = stat.daily_cap_total && stat.sent_today >= stat.daily_cap_total;
                const leads = Object.values(stat.enrollments).reduce((a, b) => a + b, 0);
                return (
                  <tr key={campaign.id}>
                    <td>{campaign.name}</td>
                    <td>
                      <span className="unibox-badge">
                        {t(`outreach.campaign.statuses.${campaign.status}`, campaign.status)}
                      </span>
                    </td>
                    <td>{campaign.steps.length}</td>
                    <td>{leads}</td>
                    <td>{stat.sends.sent || 0}</td>
                    <td>
                      <span className={`badge ${atCap ? 'badge-warning' : 'badge-neutral'}`}>
                        {stat.daily_cap_total
                          ? `${stat.sent_today} / ${stat.daily_cap_total}`
                          : stat.sent_today}
                      </span>
                      {atCap && <InfoHint text={t('outreach.campaign.atDailyCap')} />}
                    </td>
                    <td>{stat.enrollments.stopped || 0}</td>
                    <td>
                      {campaign.status === 'active' ? (
                        <button className="unibox-page-button" onClick={() => changeStatus(campaign, 'paused')}>
                          <Pause size={13} /> {t('outreach.campaign.pause')}
                        </button>
                      ) : (
                        <button className="unibox-page-button" onClick={() => changeStatus(campaign, 'active')}>
                          <Play size={13} /> {t('outreach.campaign.start')}
                        </button>
                      )}{' '}
                      <button className="unibox-page-button" onClick={() => setEditing(campaign)}>
                        <Settings2 size={13} /> {t('outreach.campaign.settings')}
                      </button>{' '}
                      <button className="unibox-page-button" onClick={() => remove(campaign)}>
                        <Trash2 size={13} />
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}

        {!loading && reports.length > 0 && (
          <div className="analytics-body campaigns-analytics">
            <h2 className="campaigns-analytics-title">{t('outreach.analytics.title')}</h2>
            {reports.map((report) => (
              <CampaignReport key={report.campaign_id} report={report} t={t} />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
